import { SurchargeGatewayMessage } from '../../../../../types/SurchargeGatewayMessage';
import { IconButton } from '../../../../cdl/IconButton/IconButton';
import { TableBuilder } from '../../../../cdl/TableBuilder/TableBuilder';
import { TableBuilderColumn } from '../../../../cdl/TableBuilder/TableBuilderColumn';
import { useToasts } from '../../../../cdl/Toast/useToasts';
import { GenericError } from '../../../../common/Error/GenericError';
import { formatCompanyName } from '../../../../common/helpers/formatCompanyName.helper';
import { formatPrice } from '../../../../common/helpers/formatPrice.helper';
import { translate } from '../../../../common/helpers/translate.helper';
import { Characters } from '../../../../common/helpers/unicode.helper';
import { useEnums } from '../../../../common/hooks/useEnums';
import { IconTrash } from '../../../../common/icons/cdl/Trash';
import { LoadingIndicator } from '../../../../common/LoadingIndicator/LoadingIndicator';
import { TableEmptyState } from '../../../../common/TableEmptyState/TableEmptyState';
import { useSurchargeDelete } from '../hooks/useSurchargeDelete';
import { useSurcharges } from '../hooks/useSurcharges';

interface SurchargesTableProps {
    groupId: string;
}

export const SurchargesTable = ({ groupId }: SurchargesTableProps) => {
    const surchargesQuery = useSurcharges(groupId);
    const surchargeDeleteMutation = useSurchargeDelete(groupId);
    const { addErrorToast } = useToasts();
    const { getHumanReadableValue } = useEnums();

    if (surchargesQuery.isPending) {
        return <LoadingIndicator />;
    }

    if (surchargesQuery.isError) {
        return <GenericError />;
    }

    if (!surchargesQuery.data.length) {
        return <TableEmptyState text={translate('surcharges.emptylist')} />;
    }

    const removeSurcharge = (id: string) => {
        surchargeDeleteMutation.mutate(id, {
            onError: () => {
                addErrorToast('surcharges.toasts.deleteSurchargeError');
            },
        });
    };

    return (
        <TableBuilder data={surchargesQuery.data}>
            <TableBuilderColumn<SurchargeGatewayMessage> header={translate('surcharges.customer')} width="30%">
                {(row) => (row.customer ? formatCompanyName({ company: row.customer }) : Characters.EN_DASH)}
            </TableBuilderColumn>

            <TableBuilderColumn<SurchargeGatewayMessage> header={translate('surcharges.packtype')}>
                {(row) => getHumanReadableValue('packType', row.packType)}
            </TableBuilderColumn>

            <TableBuilderColumn<SurchargeGatewayMessage> header={translate('surcharges.group')}>
                {(row) => (row.group ? getHumanReadableValue('productGroup', row.group) : Characters.EN_DASH)}
            </TableBuilderColumn>

            <TableBuilderColumn<SurchargeGatewayMessage> numeric header={translate('surcharges.value')}>
                {(row) => formatPrice(row.value)}
            </TableBuilderColumn>
            <TableBuilderColumn<SurchargeGatewayMessage> width="5%" header={''}>
                {(row) => <IconButton destructive Icon={IconTrash} onClick={() => removeSurcharge(row.id)} />}
            </TableBuilderColumn>
        </TableBuilder>
    );
};
